import React from 'react'
import { useLocation } from 'react-router'
import { Route, Switch } from 'react-router-dom'

import {
  EcommercePage,
  FilePage,
  MenuPage as MenuPageType,
  PageType as PageTypeEnum,
  Redirect,
  TemplatePage
} from 'src/apollo/sites'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { RequestContext } from 'src/lib/js/context'
import { getHost, getPath } from 'src/lib/js/utilities'

import RedirectWithStatus from 'shared/components/common/RedirectWithStatus'
import NotFound404 from 'shared/components/errors/NotFound404'
import { RxDataUsage } from 'shared/js/hooks/useDefaultSiteData'
import { AdditionalPagesType } from 'shared/js/types'

import AccountPage from 'public/components/default_template/AccountPage'
import CheckoutPage from 'public/components/default_template/CheckoutPage'
import ConfirmationPage from 'public/components/default_template/ConfirmationPage'
import EcommCheckoutPage from 'public/components/default_template/EcommCheckoutPage'
import EcommConfirmationPage from 'public/components/default_template/EcommConfirmationPage'
import EcommPage from 'public/components/default_template/EcommPage'
import MenuPage from 'public/components/default_template/MenuPage'
import OrderPage from 'public/components/default_template/OrderPage'
import MainPage, {
  EditPathRoot
} from 'public/components/default_template/main_page/MainPage'
import { generateAddlPageMetaFields } from 'public/components/default_template/main_page/generateMetaFields'
import { SiftContextProvider } from 'public/components/default_template/meta/sift/SiftContext'
import ProductDisplayPage from 'public/components/default_template/product_display/ecommerce_product_display/ProductDisplayPage'
import CategoryDisplayPage from 'public/components/default_template/category_display/CategoryDisplayPage'
import { DisplayPageContextProvider } from 'public/components/default_template/DisplayPageContext'
import { CustomerContextProvider } from 'public/components/online_ordering/CustomerContext'
import { EcommerceProvidersWrapper } from 'public/components/pages/EcommerceProvidersWrapper'
import { EcommercePageShell } from 'public/components/pages/EcommercePageShell'
import AppRedirect from 'public/components/pages/app_redirect/AppRedirect'
import CateringFormPage, {
  CateringFormPageType
} from 'public/components/pages/catering/CateringFormPage'
import EmailSignupPage, {
  EmailMarketingPageType
} from 'public/components/pages/email_marketing_page/EmailSignupPage'
import FormPageComponent, {
  FormPageType
} from 'public/components/pages/form_page/FormPage'
import { GiftCardPurchasePage } from 'public/components/pages/gift_card_purchase_page/GiftCardPurchasePage'
import IFramePage from 'public/components/pages/iframe_page/IFramePage'
import LocationSelectionPage from 'public/components/pages/location_selection_page/LocationSelectionPage'
import LoyaltyPage, {
  LoyaltyPageType
} from 'public/components/pages/loyalty_page/LoyaltyPage'
import MapPage from 'public/components/pages/map_page/MapPage'
import DynamicMenuPage from 'public/components/pages/menu_page/DynamicMenuPage'
import PageShell from 'public/components/pages/page_shell'
import AdyenPage from 'public/components/pages/payment_page/AdyenPage'
import PdfPage from 'public/components/pages/pdf_page/PDFPage'
import TemplatePageComponent from 'public/components/pages/template_page'
import {
  UntappdMenuPage,
  UntappdMenuPageType
} from 'public/components/pages/untappd_menu_page/UntappdMenuPage'

import { PageDomainProvider } from './PageDomainContext'
import { OrderRoutes } from './ToastOrderRoutes'
import { Channel, DEFAULT_HOME_PATH } from './siteUtilities'
import useSitePagesData from './useSitePagesData'
import { t } from 'src/translations'
import urljoin from 'url-join'
import VomProductDisplayPage from 'public/components/default_template/product_display/view_only_menu_product_display/VomProductDisplayPage'
import { ECOMM_ROUTE_SEGMENTS } from './ecommRouteConstants'

type PageType = AdditionalPagesType[0]

// Re-export for backward compatibility
export { ECOMM_ROUTE_SEGMENTS }

const SitesRoutes = ({ staticContext }: { staticContext?: RequestContext }) => {
  const { pathname, search } = useLocation()
  const host = getHost(staticContext)
  const path = getPath(staticContext)
  const { sitePagesData: siteRestaurant, loading } =
    useSitePagesData(staticContext)
  if (loading) {
    return null
  }

  if (!host || !siteRestaurant) {
    // Log 404 errors with context to help diagnose issues, especially for iframe routes
    const referrer =
      typeof window !== 'undefined'
        ? window.__REFERRER__
        : staticContext?.req?.headers.referer
    const queryParams = new URLSearchParams(search)
    reportErrorMessageWithData('SitesRoutes: 404 - site data not found', {
      host,
      path,
      pathname,
      search,
      shortUrlParam: queryParams.get('shortUrl'),
      referrer,
      hasSiteRestaurant: !!siteRestaurant,
      hasHost: !!host
    })
    return (
      <NotFound404
        meta={siteRestaurant?.meta}
        siteTheme={siteRestaurant?.theme}
      />
    )
  }

  const pages = siteRestaurant.additionalPages
  const hasMultiLocations = (siteRestaurant.locations?.length || 0) > 1
  const redirects = siteRestaurant.redirects as Array<Redirect>
  const primaryColor = siteRestaurant.meta?.primaryColor
  const meta = siteRestaurant.meta
  const theme = siteRestaurant.theme
  const pathToPage = (pageIdx: number): EditPathRoot =>
    `additionalPages[${pageIdx}]`

  const hasAdditionalHomePage = siteRestaurant.additionalPages?.some(
    (p) => p.route === DEFAULT_HOME_PATH
  )
  const hasMenuPage = !siteRestaurant.config.menuConfig?.optedOut
  const hasOoPage = !siteRestaurant.config.ooConfig?.optedOut

  const ecommBasePath = pages?.find(
    (p) =>
      'content' in p &&
      p.content &&
      '__typename' in p.content &&
      p.content.__typename === 'EcommercePage'
  )?.route

  // When ecomm is at the root and OO is also enabled, the OO routes own /checkout and
  // /confirm. Prefix the ecomm checkout/confirm routes with /store to avoid the collision.
  const ecommCheckoutConfirmPathPrefix =
    ecommBasePath === '/' &&
    siteRestaurant.config?.isOnlineOrderingEnabled &&
    hasOoPage
      ? '/store'
      : undefined

  const orderComponent = hasOoPage ? (
    <PageShell
      noPopups={!hasMultiLocations}
      titleTag={
        hasMultiLocations
          ? t('sites-routes.location-selection')
          : t('sites-routes.order-online')
      }
      ooUsage={!hasMultiLocations ? RxDataUsage.Required : RxDataUsage.Optional}
    >
      {hasMultiLocations ? <LocationSelectionPage /> : <OrderPage />}
    </PageShell>
  ) : (
    <NotFound404 meta={meta} siteTheme={theme} />
  )

  const menuComponent = hasMenuPage ? (
    <PageShell
      pageType={PageTypeEnum.Menu}
      ooUsage={RxDataUsage.Required}
      titleTag={t('sites-routes.menu')}
    >
      <MenuPage />
    </PageShell>
  ) : (
    <NotFound404 meta={meta} siteTheme={theme} />
  )

  const vomComponent = (
    <PageShell noPopups ooUsage={RxDataUsage.Required}>
      <DisplayPageContextProvider channel={Channel.OO}>
        <VomProductDisplayPage />
      </DisplayPageContextProvider>
    </PageShell>
  )

  // If opted into the default home component, render it at the configured route or DEFAULT_HOME_PATH.
  // Otherwise, if one of the additionalPages is configured to be the homepage, dont set homeComponent and the additional page will be
  // rendered at its route (i.e. DEFAULT_HOME_PATH) when all the other pages are added to the router below.
  // If no additionPage is configured to be the homepage, render the OO or menu page at DEFAULT_HOME_PATH.
  let homeComponent = null
  let homeComponentRoute = ''
  if (!siteRestaurant.config.homePageConfig?.optedOut) {
    // MainPage needs OO data to load the Promo/Loyalty banners, but failing to load OO shouldnt cause MainPage to fail.
    homeComponent = (
      <PageShell
        pageType={PageTypeEnum.Template}
        ooUsage={RxDataUsage.Optional}
      >
        <MainPage />
      </PageShell>
    )
    homeComponentRoute =
      siteRestaurant.config.homePageConfig?.route ?? DEFAULT_HOME_PATH
  } else if (!hasAdditionalHomePage) {
    homeComponentRoute = DEFAULT_HOME_PATH
    if (hasOoPage) {
      homeComponent = orderComponent
    } else if (hasMenuPage) {
      homeComponent = menuComponent
    } else {
      homeComponent = <NotFound404 meta={meta} siteTheme={theme} />
    }
  }

  const verifiedDomains = siteRestaurant.domainsV2.filter(
    (d) => !d.pendingVerification
  )

  if (siteRestaurant.config?.isMenuOnly && hasMenuPage) {
    return (
      <CustomerContextProvider>
        <PageDomainProvider
          domains={verifiedDomains}
          host={host}
          pathname={pathname}
        >
          <SiftContextProvider>
            <Switch>
              {redirects.map(({ from, to, status }) => (
                <Route
                  key={from}
                  exact
                  path={from}
                  component={() => (
                    <RedirectWithStatus to={to} status={status} />
                  )}
                />
              ))}
              <Route
                exact
                path={DEFAULT_HOME_PATH}
                component={() => menuComponent}
              />
              <Route
                exact
                path='/menu/:slug?/:groupSlug(group_.+)\/:itemSlug(item-[^_]+_.+)?'
                component={() => vomComponent}
              />
              <Route
                exact
                path='/menu/:slug/:menuSlug([^_]+_)?:menuGuid?'
                component={() => menuComponent}
              />
              <Route exact path='/menu/:slug' component={() => menuComponent} />
              <Route exact path='/menu' component={() => menuComponent} />
              <Route
                exact
                path='/map'
                component={() => (
                  <MapPage color={primaryColor} siteTheme={theme} />
                )}
              />
              <Route
                exact
                path='/sites-web/v1/map'
                component={() => (
                  <MapPage color={primaryColor} siteTheme={theme} />
                )}
              />
              <Route
                component={() => (
                  <NotFound404 meta={siteRestaurant?.meta} siteTheme={theme} />
                )}
              />
            </Switch>
          </SiftContextProvider>
        </PageDomainProvider>
      </CustomerContextProvider>
    )
  }

  if (
    siteRestaurant.config?.isOnlineOrderingOnly &&
    siteRestaurant.config?.isOnlineOrderingEnabled
  ) {
    return (
      <OrderRoutes
        staticContext={staticContext}
        sitesUsage={RxDataUsage.Required}
        accountPath='/account'
      />
    )
  }

  const routes = [
    ...(homeComponentRoute && homeComponent
      ? [{ path: homeComponentRoute, component: () => homeComponent }]
      : []),
    {
      path: '/brandedAppRedirect',
      component: () => (
        <PageShell ooUsage={RxDataUsage.None}>
          <AppRedirect />
        </PageShell>
      )
    },
    {
      path: '/map',
      component: () => <MapPage color={primaryColor} siteTheme={theme} />
    },
    {
      path: '/sites-web/v1/map',
      component: () => <MapPage color={primaryColor} siteTheme={theme} />
    },
    { path: '/iframe', component: () => <IFramePage /> },
    { path: '/sites-web/v1/iframe', component: () => <IFramePage /> },
    { path: '/adyen', component: () => <AdyenPage /> },
    { path: '/sites-web/v1/adyen', component: () => <AdyenPage /> },
    {
      path: '/menu/:slug?/:groupSlug(group_.+)\\/:itemSlug(item-[^_]+_.+)?',
      component: () => vomComponent
    }
  ]

  if (hasMenuPage) {
    routes.push({
      path: '/menu/:slug/:menuSlug([^_]+_)?:menuGuid?',
      component: () => menuComponent
    })
    routes.push({ path: '/menu/:slug', component: () => menuComponent })
    routes.push({ path: '/menu', component: () => menuComponent })
  }

  // Collect the ecomm routes and add them last to better support stores at the homepage w/ multi-location
  // Split into three arrays:
  // 1. Routes without slug capture (checkout/confirm) - no :slug? in parent or path
  // 2. Routes without location slug in path - no :slug? in route path, rendered outside :slug? parent
  // 3. Routes with location slug in path - has :slug? in route path, rendered under :slug? parent
  const ecommerceRoutesWithoutSlug: Array<{
    path: string
    component: () => React.ReactElement
  }> = []
  const ecommerceRoutesNoLocation: Array<{
    path: string
    component: () => React.ReactElement
  }> = []
  const ecommerceRoutesWithLocation: Array<{
    path: string
    component: () => React.ReactElement
  }> = []

  pages?.forEach((page: PageType, pageIdx: number) => {
    const route = page.route.startsWith('/') ? page.route : `/${page.route}`
    const pageSeoMeta = generateAddlPageMetaFields(
      page.meta,
      page.route,
      siteRestaurant.name
    )

    if (page.content.__typename === 'FilePage') {
      const content = page.content as FilePage
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.File}
            titleTag={pageSeoMeta.title || content.name}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Optional}
          >
            <PdfPage file={content.filePath} config={page.config} />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'TemplatePage') {
      const content = page.content as TemplatePage
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.Template}
            ooUsage={RxDataUsage.Optional}
          >
            <TemplatePageComponent
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              config={page.config}
              pageSeoMeta={pageSeoMeta}
              bulkCalls={page.bulkCalls}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'FormPage') {
      const content = page.content as FormPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.Form}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Optional}
          >
            <FormPageComponent
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              id={page.route}
              config={page.config}
              title={page.meta?.title || page.route}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'LoyaltySignUpPage') {
      const content = page.content as LoyaltyPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.LoyaltySignUp}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Optional}
          >
            <LoyaltyPage
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              config={page.config}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'EmailMarketingPage') {
      const content = page.content as EmailMarketingPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.EmailMarketing}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Optional}
          >
            <EmailSignupPage
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              config={page.config}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'CateringFormPage') {
      const content = page.content as CateringFormPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.CateringForm}
            titleTag={page.meta?.title || page.route.replace('/', '')}
            ooUsage={RxDataUsage.Optional}
          >
            <CateringFormPage
              rxGuid={
                siteRestaurant?.locations
                  ? siteRestaurant?.locations[0]?.externalId
                  : ''
              }
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              config={page.config}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'UntappdMenuPage') {
      const content = page.content as UntappdMenuPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.UntappdMenu}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Optional}
          >
            <UntappdMenuPage
              content={content}
              editPathRoot={pathToPage(pageIdx)}
              config={page.config}
            />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'GiftCardPurchasePage') {
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.GiftCardPurchase}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Required}
          >
            <GiftCardPurchasePage config={page.config} />
          </PageShell>
        )
      })
    } else if (page.content.__typename === 'EcommercePage') {
      const content = page.content as EcommercePage
      // Note: EcommercePageShell provides PageShell infrastructure without duplicate providers
      // Contexts are provided by EcommerceProvidersWrapper at route level
      const ecommComponent = (
        <EcommercePageShell
          titleTag={pageSeoMeta.title}
          descriptionTag={pageSeoMeta.description}
          staticContext={staticContext}
        >
          <EcommPage ecommContent={content} />
        </EcommercePageShell>
      )
      const categoryPageComponent = (
        <EcommercePageShell staticContext={staticContext}>
          <CategoryDisplayPage ecommContent={content} />
        </EcommercePageShell>
      )
      const productDisplayPageComponent = (
        <EcommercePageShell
          titleTag={t('sites-routes.product-display')}
          staticContext={staticContext}
        >
          <DisplayPageContextProvider channel={Channel.ECOMM}>
            <ProductDisplayPage ecommContent={content} />
          </DisplayPageContextProvider>
        </EcommercePageShell>
      )
      // Routes without slug parameter (checkout/confirm) - these should not capture location slugs
      const ecommRoutesWithoutSlug: Array<{
        path: string
        component: () => React.ReactElement
      }> = [
        {
          path: constructValidSiteRoute(
            ecommCheckoutConfirmPathPrefix || '',
            route,
            '/checkout'
          ),
          component: () => (
            <EcommercePageShell
              titleTag={t('sites-routes.checkout')}
              staticContext={staticContext}
            >
              <EcommCheckoutPage />
            </EcommercePageShell>
          )
        },
        {
          path: constructValidSiteRoute(
            ecommCheckoutConfirmPathPrefix || '',
            route,
            '/confirm/:pathOrderGuid'
          ),
          component: () => (
            <EcommercePageShell
              titleTag={t('sites-routes.order-confirmation')}
              staticContext={staticContext}
            >
              <EcommConfirmationPage />
            </EcommercePageShell>
          )
        },
        {
          path: constructValidSiteRoute(
            ecommCheckoutConfirmPathPrefix || '',
            route,
            '/confirm'
          ),
          component: () => (
            <EcommercePageShell
              titleTag={t('sites-routes.order-confirmation')}
              staticContext={staticContext}
            >
              <EcommConfirmationPage />
            </EcommercePageShell>
          )
        }
      ]

      // Routes without location slug in path - render directly without :slug? parent
      const ecommRoutesNoLocation: Array<{
        path: string
        component: () => React.ReactElement
      }> = [
        { path: route, component: () => ecommComponent },
        {
          path: constructValidSiteRoute(route, '/add/:guid(.+)/:ignore'),
          component: () => ecommComponent
        },
        {
          path: constructValidSiteRoute(route, '/categories/:categorySlug'),
          component: () => categoryPageComponent
        },
        // Variation group routes MUST come before general /products/:itemSlug
        // so /products/v/... matches the specific pattern first
        {
          path: constructValidSiteRoute(route, '/products/v/:variationSlug?'),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(
            route,
            '/products/v/:variationSlug?/:itemSlug?'
          ),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(route, '/products/:itemSlug'),
          component: () => productDisplayPageComponent
        }
      ]

      // Routes with location slug in path - render under :slug? parent for multi-location
      const ecommRoutesWithLocation: Array<{
        path: string
        component: () => React.ReactElement
      }> = [
        {
          path: constructValidSiteRoute(
            route,
            '/:slug?/categories/:categorySlug'
          ),
          component: () => categoryPageComponent
        },
        {
          path: constructValidSiteRoute(
            route,
            '/:slug?/products/v/:variationSlug?'
          ),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(
            route,
            '/:slug?/products/v/:variationSlug?/:itemSlug?'
          ),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(route, '/:slug?/products/:itemSlug'),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(
            route,
            '/:slug?/:groupSlug(group_.+)\\/:itemSlug(item-[^_]+_.+)?'
          ),
          component: () => productDisplayPageComponent
        },
        {
          path: constructValidSiteRoute(route, '/:slug'),
          component: () => ecommComponent
        }
      ]

      ecommerceRoutesWithoutSlug.push(...ecommRoutesWithoutSlug)
      ecommerceRoutesNoLocation.push(...ecommRoutesNoLocation)
      ecommerceRoutesWithLocation.push(...ecommRoutesWithLocation)
    } else if (page.content.__typename === 'MenuPage') {
      const content = page.content as MenuPageType
      routes.push({
        path: route,
        component: () => (
          <PageShell
            pageType={PageTypeEnum.Menu}
            titleTag={pageSeoMeta.title}
            descriptionTag={pageSeoMeta.description}
            ooUsage={RxDataUsage.Required}
          >
            <DynamicMenuPage
              content={content}
              config={page.config}
              bulkCalls={page.bulkCalls}
              editPathRoot={pathToPage(pageIdx)}
            />
          </PageShell>
        )
      })
    }
  })

  if (siteRestaurant.config?.isOnlineOrderingEnabled && hasOoPage) {
    const orderRoutes = [
      { path: '/order', component: () => orderComponent },
      {
        path: '/order/:slug/:itemSlug(item-[^_]+_.+)?',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-online')}
            ooUsage={RxDataUsage.Required}
          >
            <OrderPage />
          </PageShell>
        )
      },
      {
        path: '/order/add/:guid(.+)/:ignore',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-online')}
            ooUsage={RxDataUsage.Required}
          >
            <OrderPage />
          </PageShell>
        )
      },
      {
        path: '/order/:slug/:menuSlug([^_]+_)?:menuGuid?',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-online')}
            ooUsage={RxDataUsage.Required}
          >
            <OrderPage />
          </PageShell>
        )
      },
      {
        path: '/order/:slug',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-online')}
            ooUsage={RxDataUsage.Required}
          >
            <OrderPage />
          </PageShell>
        )
      },
      {
        path: '/checkout',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.checkout')}
            ooUsage={RxDataUsage.Required}
          >
            <CheckoutPage />
          </PageShell>
        )
      },
      {
        path: '/confirm',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-confirmation')}
            ooUsage={RxDataUsage.Required}
          >
            <ConfirmationPage />
          </PageShell>
        )
      },
      {
        path: '/confirm/:pathOrderGuid',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.order-confirmation')}
            ooUsage={RxDataUsage.Required}
          >
            <ConfirmationPage />
          </PageShell>
        )
      },
      {
        path: '/account',
        component: () => (
          <PageShell
            noPopups
            titleTag={t('sites-routes.my-account')}
            ooUsage={RxDataUsage.Required}
          >
            <AccountPage />
          </PageShell>
        )
      }
    ]

    routes.push(...orderRoutes)
  }

  // Don't push ecommerce routes into main routes array - they'll be rendered separately

  const routesByPath = new Map(routes.map((r) => [r.path, r]))
  const currentDomain = verifiedDomains.find((d) => d.domain === host)
  // a domain with a page route renders that route's component a the root
  const routeOverride = currentDomain?.pageRoute
    ? routesByPath.get(currentDomain.pageRoute)
    : null

  return (
    <CustomerContextProvider>
      <PageDomainProvider
        domains={verifiedDomains}
        host={host}
        pathname={pathname}
      >
        <SiftContextProvider>
          <Switch>
            {/* when the domain is mapped to a page, this should take priority over redirects */}
            {routeOverride ? (
              <Route
                exact
                path={DEFAULT_HOME_PATH}
                component={routeOverride.component}
              />
            ) : null}
            {redirects.map(({ from, to, status }) => (
              <Route
                key={from}
                exact
                path={from}
                component={() => <RedirectWithStatus to={to} status={status} />}
              />
            ))}
            {routes.map((r, i) => (
              <Route
                exact
                key={`${r.path}-${i}`}
                path={r.path}
                component={r.component}
              />
            ))}
            {/* Ecommerce routes without slug parameter (checkout/confirm) - match FIRST to avoid :slug? capturing them */}
            {ecommerceRoutesWithoutSlug.length > 0 &&
              ecommBasePath &&
              (() => {
                const noSlugRouteContent = (
                  <EcommerceProvidersWrapper
                    ecommPath={ecommBasePath}
                    ecommCheckoutPathPrefix={ecommCheckoutConfirmPathPrefix}
                    ecommConfirmPathPrefix={ecommCheckoutConfirmPathPrefix}
                  >
                    <Switch>
                      {ecommerceRoutesWithoutSlug.map((r, i) => (
                        <Route
                          exact
                          key={`ecomm-no-slug-${r.path}-${i}`}
                          path={r.path}
                          component={r.component}
                        />
                      ))}
                    </Switch>
                  </EcommerceProvidersWrapper>
                )

                return (
                  <Route
                    path={constructValidSiteRoute(
                      ecommCheckoutConfirmPathPrefix || '',
                      ecommBasePath,
                      '(checkout|confirm)'
                    )}
                    render={() => noSlugRouteContent}
                  />
                )
              })()}
            {/* All ecommerce routes under single :slug? parent */}
            {/* Routes are ordered by specificity: noLocation routes (more specific) before withLocation routes */}
            {(ecommerceRoutesNoLocation.length > 0 ||
              ecommerceRoutesWithLocation.length > 0) &&
              ecommBasePath &&
              (() => {
                const allEcommRouteContent = (
                  <EcommerceProvidersWrapper
                    ecommPath={ecommBasePath}
                    ecommCheckoutPathPrefix={ecommCheckoutConfirmPathPrefix}
                    ecommConfirmPathPrefix={ecommCheckoutConfirmPathPrefix}
                  >
                    <Switch>
                      {/* Routes without location slug come first - they're more specific */}
                      {ecommerceRoutesNoLocation.map((r, i) => (
                        <Route
                          exact
                          key={`ecomm-no-loc-${r.path}-${i}`}
                          path={r.path}
                          component={r.component}
                        />
                      ))}
                      {/* Routes with location slug come after - they're more general */}
                      {ecommerceRoutesWithLocation.map((r, i) => (
                        <Route
                          exact
                          key={`ecomm-${r.path}-${i}`}
                          path={r.path}
                          component={r.component}
                        />
                      ))}
                      <Route
                        render={() => (
                          <NotFound404
                            meta={siteRestaurant?.meta}
                            siteTheme={siteRestaurant?.theme}
                          />
                        )}
                      />
                    </Switch>
                  </EcommerceProvidersWrapper>
                )

                return (
                  <Route
                    path={constructValidSiteRoute(ecommBasePath, '/:slug?')}
                    render={() => allEcommRouteContent}
                  />
                )
              })()}
            <Route
              component={() => (
                <NotFound404
                  meta={siteRestaurant?.meta}
                  siteTheme={siteRestaurant?.theme}
                />
              )}
            />
          </Switch>
        </SiftContextProvider>
      </PageDomainProvider>
    </CustomerContextProvider>
  )
}

// url-join is not compatible with react-router notation so we need to ensure the route
// doesn't have multiple consecutive slashes ourselves
export const constructValidSiteRoute = (...segments: string[]) => {
  return segments
    .filter((s) => s !== '')
    .reduce((acc, segment) => {
      const trimmed = segment.startsWith('/') ? segment.slice(1) : segment
      return `${urljoin(acc, '/')}${trimmed}`
    })
}

export default SitesRoutes
